// --- FILE: src/pages/VendedoresPage.jsx ---
// Gestión de vendedores/empleados: alta con ID de DynamiaERP + PIN para marcar turnos,
// buscador y aviso de quiénes están en DynamiaERP pero faltan por registrar aquí.
// Escritura vía Cloud Functions (saveSeller/deleteSeller) porque firestore.rules
// bloquea escribir `sellers` desde el cliente.

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Users, Plus, Pencil, Trash2, RefreshCw, EyeOff, Eye, Search, X,
  UserPlus, AlertTriangle, Link2, Shuffle, KeyRound,
} from 'lucide-react';
import DashboardHeader from '@/components/dashboard/DashboardHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { useToast } from '@/hooks/useToast';
import { FunctionsService } from '@/services/functions.service';

const EMPTY_SELLER = {
  id: '',
  name: '',
  pin: '',
  dynamiaSellerId: '',
  hourlyRate: '10.50',
  active: true,
};

// "José  Pagán" / "JOSE PAGAN" → "jose pagan"
const normalizeName = (s) => (s || '')
  .normalize('NFD').replace(/[̀-ͯ]/g, '')
  .toLowerCase().replace(/[^a-z0-9 ]/g, ' ').replace(/\s+/g, ' ').trim();

const inputClass = 'bg-white/5 border-white/10 text-white placeholder:text-white/25';

export default function VendedoresPage() {
  const [sellers, setSellers] = useState([]);
  const [dynamiaSellers, setDynamiaSellers] = useState([]);
  const [dynamiaError, setDynamiaError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [form, setForm] = useState(EMPTY_SELLER);
  const [isNew, setIsNew] = useState(true);
  const [search, setSearch] = useState('');
  const [showHidden, setShowHidden] = useState(false);
  const [showPins, setShowPins] = useState(false);
  const { toast, toasts, dismiss } = useToast();

  const loadSellers = useCallback(async () => {
    setLoading(true);
    const result = await FunctionsService.getSellersOverview();
    if (result.data) {
      setSellers([...result.data.sellers].sort((a, b) => a.name.localeCompare(b.name)));
      setDynamiaSellers(result.data.dynamiaSellers || []);
      setDynamiaError(result.data.dynamiaError || null);
    } else {
      toast({ title: 'Error al cargar', description: result.error, variant: 'destructive' });
    }
    setLoading(false);
  }, [toast]);

  useEffect(() => {
    loadSellers();
  }, [loadSellers]);

  // IDs de DynamiaERP ya registrados en la app
  const registeredIds = useMemo(
    () => new Set(sellers.filter(s => s.dynamiaSellerId != null).map(s => String(s.dynamiaSellerId))),
    [sellers]
  );
  const dynamiaIds = useMemo(() => new Set(dynamiaSellers.map(d => String(d.id))), [dynamiaSellers]);

  // Vendedores activos en DynamiaERP que faltan por registrar. Si hay un empleado
  // sin ID con el mismo nombre, se sugiere vincularlo en vez de duplicarlo.
  const missing = useMemo(() => dynamiaSellers
    .filter(d => d.active && !registeredIds.has(String(d.id)))
    .map(d => ({
      ...d,
      linkCandidate: sellers.find(s => s.dynamiaSellerId == null && normalizeName(s.name) === normalizeName(d.name)) || null,
    })),
  [dynamiaSellers, registeredIds, sellers]);

  const withoutPin = sellers.filter(s => s.active && !s.pin).length;
  const hiddenCount = sellers.filter(s => !s.active).length;

  const visibleSellers = useMemo(() => {
    const q = normalizeName(search);
    return sellers
      .filter(s => showHidden || s.active)
      .filter(s => !q
        || normalizeName(s.name).includes(q)
        || String(s.dynamiaSellerId ?? '').includes(q)
        || s.id.includes(q));
  }, [sellers, search, showHidden]);

  const filteredMissing = useMemo(() => {
    const q = normalizeName(search);
    return q ? missing.filter(m => normalizeName(m.name).includes(q) || String(m.id).includes(q)) : missing;
  }, [missing, search]);

  function openAdd(prefill = {}) {
    setForm({ ...EMPTY_SELLER, ...prefill });
    setIsNew(true);
    setDialogOpen(true);
  }

  function openEdit(seller, overrides = {}) {
    setForm({
      id: seller.id,
      name: seller.name,
      pin: seller.pin || '',
      dynamiaSellerId: seller.dynamiaSellerId != null ? String(seller.dynamiaSellerId) : '',
      hourlyRate: String(seller.hourlyRate ?? '10.50'),
      active: seller.active !== false,
      ...overrides,
    });
    setIsNew(false);
    setDialogOpen(true);
  }

  // Al escribir el ID en un alta, autocompleta el nombre desde DynamiaERP
  function handleDynamiaIdChange(value) {
    const clean = value.replace(/\D/g, '');
    setForm(f => {
      const match = dynamiaSellers.find(d => String(d.id) === clean);
      return { ...f, dynamiaSellerId: clean, name: isNew && match && !f.name.trim() ? match.name : f.name };
    });
  }

  function generatePin() {
    const used = new Set(sellers.filter(s => s.id !== form.id).map(s => String(s.pin)));
    for (let i = 0; i < 200; i++) {
      const pin = String(Math.floor(1000 + Math.random() * 9000));
      if (!used.has(pin)) {
        setForm(f => ({ ...f, pin }));
        return;
      }
    }
  }

  const pinConflict = form.pin.length === 4
    ? sellers.find(s => s.id !== form.id && String(s.pin) === form.pin)
    : null;
  const idConflict = form.dynamiaSellerId
    ? sellers.find(s => s.id !== form.id && String(s.dynamiaSellerId) === form.dynamiaSellerId)
    : null;
  const dynamiaMatch = form.dynamiaSellerId
    ? dynamiaSellers.find(d => String(d.id) === form.dynamiaSellerId)
    : null;

  async function handleSave() {
    if (!form.dynamiaSellerId || !form.name.trim()) {
      toast({ title: 'Faltan campos', description: 'ID de DynamiaERP y nombre son obligatorios', variant: 'destructive' });
      return;
    }
    if (!/^\d{4}$/.test(form.pin)) {
      toast({ title: 'PIN inválido', description: 'El PIN debe tener exactamente 4 dígitos', variant: 'destructive' });
      return;
    }
    if (pinConflict || idConflict) {
      toast({
        title: 'Datos duplicados',
        description: pinConflict ? `El PIN ya lo usa ${pinConflict.name}` : `El ID ya está asignado a ${idConflict.name}`,
        variant: 'destructive',
      });
      return;
    }
    setSaving(true);
    const result = await FunctionsService.saveSeller({
      id: isNew ? undefined : form.id,
      name: form.name,
      pin: form.pin,
      dynamiaSellerId: form.dynamiaSellerId,
      hourlyRate: form.hourlyRate,
      active: form.active,
    });
    if (result.data?.success) {
      toast({ title: 'Guardado', description: `${form.name.trim()} ya puede marcar turnos con su PIN` });
      setDialogOpen(false);
      await loadSellers();
    } else {
      toast({ title: 'Error al guardar', description: result.error ?? 'Error desconocido', variant: 'destructive' });
    }
    setSaving(false);
  }

  async function handleToggleActive(seller) {
    if (!seller.pin || seller.dynamiaSellerId == null) {
      openEdit(seller, { active: !seller.active });
      return;
    }
    const newActive = !seller.active;
    const result = await FunctionsService.saveSeller({ ...seller, active: newActive });
    if (result.data?.success) {
      setSellers(prev => prev.map(s => s.id === seller.id ? { ...s, active: newActive } : s));
    } else {
      toast({ title: 'Error', description: result.error ?? 'No se pudo actualizar', variant: 'destructive' });
    }
  }

  async function handleDelete() {
    if (!deleteTarget) return;
    const result = await FunctionsService.deleteSeller(deleteTarget.id);
    if (result.data?.success) {
      toast({ title: 'Eliminado', description: `${deleteTarget.name} eliminado` });
      setDeleteTarget(null);
      await loadSellers();
    } else {
      toast({ title: 'Error al eliminar', description: result.error ?? 'Error desconocido', variant: 'destructive' });
    }
  }

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      {/* Toasts */}
      <div className="fixed top-4 right-4 z-[100] flex flex-col gap-2">
        {toasts.map((t) => (
          <div key={t.id} onClick={() => dismiss(t.id)}
            className={`cursor-pointer rounded-lg px-4 py-3 shadow-lg text-sm max-w-xs ${t.variant === 'destructive' ? 'bg-red-900/90 text-red-100' : 'bg-gray-800 text-white'}`}>
            <p className="font-semibold">{t.title}</p>
            {t.description && <p className="text-white/70 mt-0.5">{t.description}</p>}
          </div>
        ))}
      </div>

      <div className="mx-auto w-full max-w-[1400px] px-4 py-6 sm:px-6 lg:px-8">
        <DashboardHeader activeTab="vendedores" title="Vendedores" subtitle="Empleados, ID de DynamiaERP y PIN para marcar turnos" />

        {/* Controles */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div className="flex flex-wrap items-center gap-3">
            <Users className="h-5 w-5 text-blue-400" />
            <span className="text-white/50 text-sm">
              {sellers.filter(s => s.active).length} activo{sellers.filter(s => s.active).length !== 1 ? 's' : ''}
              {hiddenCount > 0 && !showHidden && (
                <span className="ml-2 text-white/30">({hiddenCount} inactivo{hiddenCount !== 1 ? 's' : ''})</span>
              )}
            </span>
            {hiddenCount > 0 && (
              <button onClick={() => setShowHidden(v => !v)}
                className={`flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border transition-colors ${showHidden
                  ? 'bg-amber-500/20 border-amber-500/40 text-amber-300'
                  : 'bg-white/5 border-white/10 text-white/40 hover:text-white/60'}`}>
                <EyeOff className="h-3 w-3" />
                {showHidden ? 'Mostrando inactivos' : 'Mostrar inactivos'}
              </button>
            )}
            {withoutPin > 0 && (
              <span className="text-xs px-2.5 py-1 rounded-full border bg-red-500/10 border-red-500/30 text-red-300">
                {withoutPin} sin PIN
              </span>
            )}
          </div>
          <div className="flex gap-2">
            <Button onClick={loadSellers} disabled={loading}
              className="bg-white/5 border border-white/10 text-white hover:bg-white/10 h-9 px-3">
              <RefreshCw className={`h-4 w-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
              Actualizar
            </Button>
            <Button onClick={() => openAdd()} className="bg-blue-600 hover:bg-blue-700 text-white h-9 px-3">
              <Plus className="h-4 w-4 mr-1.5" />
              Agregar
            </Button>
          </div>
        </div>

        {/* Buscador */}
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/30" />
          <Input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Buscar por nombre o ID de DynamiaERP…"
            className={`${inputClass} pl-9 pr-9 h-10`}
          />
          {search && (
            <button onClick={() => setSearch('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white">
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        {dynamiaError && (
          <div className="flex items-start gap-2 bg-amber-500/10 border border-amber-500/30 rounded-lg px-4 py-3 mb-4 text-sm text-amber-200">
            <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
            <span>No se pudo consultar DynamiaERP para detectar vendedores faltantes: {dynamiaError}</span>
          </div>
        )}

        {/* Faltan por registrar */}
        {!loading && filteredMissing.length > 0 && (
          <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-4 mb-6">
            <div className="flex items-center gap-2 mb-3">
              <UserPlus className="h-4 w-4 text-amber-400" />
              <h3 className="font-semibold text-amber-200 text-sm">
                Faltan por registrar ({filteredMissing.length})
              </h3>
              <span className="text-white/40 text-xs hidden sm:inline">
                — están activos en DynamiaERP pero no pueden marcar turnos
              </span>
            </div>
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {filteredMissing.map(m => (
                <div key={m.id} className="flex items-center justify-between gap-3 rounded-lg border border-white/10 bg-gray-900/60 px-3 py-2">
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{m.name || 'Sin nombre'}</p>
                    <p className="text-xs text-white/40 font-mono">ID {m.id}</p>
                  </div>
                  {m.linkCandidate ? (
                    <Button onClick={() => openEdit(m.linkCandidate, { dynamiaSellerId: String(m.id) })}
                      className="bg-purple-600/80 hover:bg-purple-600 text-white h-8 px-3 text-xs shrink-0"
                      title={`Asignar este ID a ${m.linkCandidate.name}`}>
                      <Link2 className="h-3.5 w-3.5 mr-1" />Vincular
                    </Button>
                  ) : (
                    <Button onClick={() => openAdd({ dynamiaSellerId: String(m.id), name: m.name })}
                      className="bg-amber-600/80 hover:bg-amber-600 text-white h-8 px-3 text-xs shrink-0">
                      <Plus className="h-3.5 w-3.5 mr-1" />Registrar
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Tabla */}
        {loading ? (
          <div className="text-center text-white/40 py-16">Cargando vendedores…</div>
        ) : (
          <div className="rounded-xl border border-white/10 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-white/5 border-b border-white/10">
                <tr>
                  <th className="px-4 py-3 text-left text-white/50 font-medium">ID Dynamia</th>
                  <th className="px-4 py-3 text-left text-white/50 font-medium">Nombre</th>
                  <th className="px-4 py-3 text-left text-white/50 font-medium">
                    <button onClick={() => setShowPins(v => !v)} className="inline-flex items-center gap-1.5 hover:text-white">
                      PIN {showPins ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                    </button>
                  </th>
                  <th className="px-4 py-3 text-right text-white/50 font-medium hidden md:table-cell">Tarifa/h</th>
                  <th className="px-4 py-3 text-center text-white/50 font-medium hidden lg:table-cell">En Dynamia</th>
                  <th className="px-4 py-3 text-center text-white/50 font-medium">Activo</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {visibleSellers.map(seller => (
                  <tr key={seller.id}
                    className={`hover:bg-white/5 transition-colors ${!seller.active ? 'opacity-40' : ''}`}>
                    <td className="px-4 py-3 font-mono text-xs text-white/50">
                      {seller.dynamiaSellerId ?? <span className="text-amber-400/70">Sin ID</span>}
                    </td>
                    <td className="px-4 py-3 font-medium">{seller.name}</td>
                    <td className="px-4 py-3 font-mono tracking-widest text-white/70">
                      {seller.pin
                        ? (showPins ? seller.pin : '••••')
                        : <span className="text-red-400/80 tracking-normal text-xs">Sin PIN</span>}
                    </td>
                    <td className="px-4 py-3 text-right text-white/60 hidden md:table-cell">
                      ${Number(seller.hourlyRate || 0).toFixed(2)}
                    </td>
                    <td className="px-4 py-3 text-center hidden lg:table-cell">
                      {dynamiaError
                        ? <span className="text-white/25">—</span>
                        : dynamiaIds.has(String(seller.dynamiaSellerId))
                          ? <span className="text-green-400">✓</span>
                          : <span className="text-white/30 text-xs">No activo</span>}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <Switch
                        checked={seller.active}
                        onCheckedChange={() => handleToggleActive(seller)}
                        className="data-[state=checked]:bg-emerald-500 data-[state=unchecked]:bg-white/20"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" onClick={() => openEdit(seller)}
                          className="h-7 w-7 p-0 bg-transparent text-white/40 hover:text-white hover:bg-white/10">
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button variant="ghost" onClick={() => setDeleteTarget(seller)}
                          className="h-7 w-7 p-0 bg-transparent text-red-400/50 hover:text-red-400 hover:bg-red-500/10">
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {visibleSellers.length === 0 && (
              <p className="text-center text-white/30 py-12">
                {search ? `Sin resultados para "${search}"` : showHidden ? 'No hay vendedores registrados' : 'No hay vendedores activos'}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Alta / edición */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-gray-900 border-white/10 text-white sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{isNew ? 'Agregar vendedor' : `Editar: ${form.name}`}</DialogTitle>
          </DialogHeader>

          <div className="grid gap-4 py-2">
            <div className="grid gap-1.5">
              <Label htmlFor="v-dyn">ID de DynamiaERP *</Label>
              <Input
                id="v-dyn"
                value={form.dynamiaSellerId}
                onChange={e => handleDynamiaIdChange(e.target.value)}
                inputMode="numeric"
                placeholder="ej. 4399"
                list="dynamia-sellers"
                className={`${inputClass} font-mono`}
              />
              <datalist id="dynamia-sellers">
                {dynamiaSellers.map(d => <option key={d.id} value={String(d.id)}>{d.name}</option>)}
              </datalist>
              {idConflict ? (
                <p className="text-xs text-red-400">Este ID ya está asignado a {idConflict.name}</p>
              ) : dynamiaMatch ? (
                <p className="text-xs text-green-400/80">En DynamiaERP: {dynamiaMatch.name}</p>
              ) : form.dynamiaSellerId && !dynamiaError ? (
                <p className="text-xs text-amber-400/80">No aparece como vendedor activo en DynamiaERP</p>
              ) : (
                <p className="text-xs text-white/30">Con este ID se asocian sus ventas del ERP</p>
              )}
            </div>

            <div className="grid gap-1.5">
              <Label htmlFor="v-name">Nombre completo *</Label>
              <Input
                id="v-name"
                value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                placeholder="ej. Jorge Rivera"
                className={inputClass}
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="grid gap-1.5">
                <Label htmlFor="v-pin">PIN de turnos *</Label>
                <div className="flex gap-1.5">
                  <Input
                    id="v-pin"
                    value={form.pin}
                    onChange={e => setForm(f => ({ ...f, pin: e.target.value.replace(/\D/g, '').slice(0, 4) }))}
                    inputMode="numeric"
                    maxLength={4}
                    placeholder="0000"
                    className={`${inputClass} font-mono tracking-[0.4em] text-center`}
                  />
                  <Button type="button" onClick={generatePin} title="Generar PIN disponible"
                    className="bg-white/5 border border-white/10 text-white hover:bg-white/10 h-10 w-10 p-0 shrink-0">
                    <Shuffle className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="v-rate">Tarifa por hora ($)</Label>
                <Input
                  id="v-rate"
                  type="number"
                  step="0.01"
                  min={0}
                  value={form.hourlyRate}
                  onChange={e => setForm(f => ({ ...f, hourlyRate: e.target.value }))}
                  className={inputClass}
                />
              </div>
            </div>
            {pinConflict ? (
              <p className="text-xs text-red-400 -mt-2">El PIN {form.pin} ya lo usa {pinConflict.name}</p>
            ) : (
              <p className="text-xs text-white/30 -mt-2 flex items-center gap-1">
                <KeyRound className="h-3 w-3" />4 dígitos, único. Lo usa en la Terminal para entrar/salir de turno.
              </p>
            )}

            <div className="flex items-center justify-between rounded-lg border border-white/10 px-4 py-3">
              <div>
                <Label htmlFor="v-active" className="cursor-pointer">Vendedor activo</Label>
                <p className="text-xs text-white/40 mt-0.5">Inactivo = no puede marcar turnos</p>
              </div>
              <Switch
                id="v-active"
                checked={form.active}
                onCheckedChange={v => setForm(f => ({ ...f, active: v }))}
                className="data-[state=checked]:bg-emerald-500 data-[state=unchecked]:bg-white/20"
              />
            </div>
          </div>

          <DialogFooter className="gap-2 pt-2">
            <Button onClick={() => setDialogOpen(false)}
              className="h-10 px-5 bg-white/5 border border-white/10 text-white hover:bg-white/10">
              Cancelar
            </Button>
            <Button onClick={handleSave} disabled={saving || !!pinConflict || !!idConflict}
              className="h-10 px-5 bg-blue-600 hover:bg-blue-700 text-white">
              {saving ? 'Guardando…' : 'Guardar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmar eliminación */}
      <Dialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <DialogContent className="bg-gray-900 border-white/10 text-white sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Eliminar a {deleteTarget?.name}</DialogTitle>
          </DialogHeader>
          <p className="text-white/60 text-sm py-2">
            Se conserva su historial de turnos. Si solo quieres que deje de marcar turnos, usa el switch
            de <strong className="text-white/80">Activo</strong>.
          </p>
          <DialogFooter className="gap-2 pt-2">
            <Button onClick={() => setDeleteTarget(null)}
              className="h-10 px-5 bg-white/5 border border-white/10 text-white hover:bg-white/10">
              Cancelar
            </Button>
            <Button onClick={handleDelete} className="h-10 px-5 bg-red-600 hover:bg-red-700 text-white">
              Eliminar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
